import React, { useContext, useEffect, useState, useCallback, Fragment, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { LayoutContext } from '../../contexts/LayoutContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import apis from '../../utils/apis';
import { RootState, useAppDispatch } from '../../store';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { deleteClient as deleteClientAPI } from '../../store/actions/ClientActions';
import { useGlobalStreaming } from '../../hooks/useGlobalStreaming';
import AttachmentViewer from '../Tasks/AttachmentViewer';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Flag from '../shared/TailwindComponents/Flag';
import Table from '../shared/TailwindComponents/Table';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import SkeletonLoader from './SkeletonLoader';
import DetailFrame from '../shared/Frames/DetailFrame';
import RecentActivityCard from '../shared/RecentActivityCard';
import CollaboratorBadge from '../shared/TailwindComponents/CollaboratorsBadge';
import ProfileImage from '../Header/ProfileImage';
import { getStatusColor, formatDate, formatDateTime, formatDocumentName } from '../../utils/functions';
import {
  PiFilePlusLight,
  PiTrashLight,
  PiFileTextLight,
  PiFolderLight,
  PiNoteLight,
  PiPencilLight,
  PiCheckCircleLight,
  PiXCircleLight,
  PiXLight,
  PiClockLight,
  PiLinkLight,
  PiMoneyLight,
  PiDownloadLight,
  PiPlusLight,
  PiSliders,
  PiTextAlignLeftLight,
  PiTagLight,
  PiCircleDashedLight,
  PiEnvelopeLight,
  PiPhoneLight,
  PiMapPinLight,
  PiCalendarBlankLight,
  PiClockClockwiseLight,
  PiClockCounterClockwiseLight,
  PiUserLight,
  PiUsersThreeLight,
  PiShieldCheckLight,
} from 'react-icons/pi';
import { Menu, Transition } from '@headlessui/react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { showConnectMatterModal, showUploadClientDocumentModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import { buildClientCollaboratorAdapter } from '../../utils/collaboratorAdapters/client';
import { fetchAllEmployees } from '../../store/actions/organization/employeeActions';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { FeatureTour, clientDetailsTourSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

// Country code to country name mapping for flag-icons library
const countryCodeMap: Record<string, string> = {
  'SE': 'SE',
  'US': 'US',
  'UK': 'GB',
  'GB': 'GB',
  'DE': 'DE',
  'FR': 'FR',
  'NO': 'NO',
  'DK': 'DK',
  'FI': 'FI',
};

// Helper to get jurisdiction display
const getJurisdictionDisplay = (jurisdiction: string | null | undefined) => {
  if (!jurisdiction) return null;
  const code = jurisdiction.toUpperCase();
  const countryCode = countryCodeMap[code] || code;
  return {
    code,
    countryCode,
  };
};

const statusToColorMap: Record<string, any> = {
  Open: 'yellow',
  Active: 'blue',
  Closed: 'gray',
  Discovery: 'blue',
  Negotiation: 'blue',
  Trial: 'blue',
  Intake: 'yellow',
};

interface RouteParams {
  clientId: string;
}

interface Client {
  id: number;
  uuid: string;
  name: string;
  type_id: number;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  notes?: string;
  is_active: boolean;
  is_individual?: boolean;
  contact_person_name?: string;
  contact_person_email?: string;
  organization_id: number;
  created_at: string;
  updated_at: string;
  client_metadata?: Record<string, any>;
}

interface Matter {
  id?: number;
  matter_id?: number;
  matter_uuid: string;
  name: string;
  status: string;
  created_at: string;
  updated_at?: string;
  category?: string;
  role?: string;
  type?: string;
  added_at?: string;
}

interface AuditLogEntry {
  id: number;
  action: string;
  details: string;
  created_by_user_name: string;
  created_at: string;
  metadata?: Record<string, any>;
}

const ClientDetails: React.FC = () => {
  const { clientId } = useParams<RouteParams>();
  const { t } = useTranslation();
  const history = useHistory();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { setIsContentScrollable } = useContext(LayoutContext);
  const dispatch = useAppDispatch();
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);
  const currentUserId = useSelector((state: RootState) => state.auth?.userId);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const { viewFile, downloadFile } = useGlobalStreaming();

  const [client, setClient] = useState<Client | null>(null);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [matterSearch, setMatterSearch] = useState('');
  const [matterFilters, setMatterFilters] = useState<{ [key: string]: string[] }>({});
  const [matterSort, setMatterSort] = useState<string>('');
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentFilters, setDocumentFilters] = useState<{ [key: string]: string[] }>({});
  const [documentSort, setDocumentSort] = useState<string>('');
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [editedClient, setEditedClient] = useState<Partial<Client> | null>(null);
  const [clientTypes, setClientTypes] = useState<any[]>([]);
  const [billing, setBilling] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [isEditingBilling, setIsEditingBilling] = useState(false);
  const [editedBilling, setEditedBilling] = useState<any>(null);
  const [lawyers, setLawyers] = useState<any[]>([]);
  const [selectedLawyerIds, setSelectedLawyerIds] = useState<string[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [isDeletingDocuments, setIsDeletingDocuments] = useState(false);
  const [lawyerProfiles, setLawyerProfiles] = useState<Record<string, any>>({});
  const [isMatterLoading, setIsMatterLoading] = useState(false);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [isAuditLogLoading, setIsAuditLogLoading] = useState(false);
  const [auditTarget, setAuditTarget] = useState<{type: string; id: string; name: string} | null>(null);

  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
  } = useFeatureTour({
    tourId: 'client-details',
    steps: clientDetailsTourSteps,
    readyToShow: !isLoading && !!client,
    autoStartDelay: 600,
  });

  // Fetch client details
  const fetchClientDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}`,
      });

      if (resp.status === 200 && resp.data?.success && resp.data?.client) {
        const clientData = resp.data.client;
        setClient(clientData);
        setEditedClient(clientData);
      } else {
        throw new Error('Failed to fetch client details');
      }
    } catch (err: any) {
      dispatch(
        showFeedbackModal({
          showModal: true,
          modalType: 'error',
          message: t('Failed to fetch client details'),
          duration: 3000,
        })
      );
    } finally {
      setIsLoading(false);
    }
  }, [clientId, dispatch, t]);

  // Fetch client types for dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await apis({
          method: 'GET',
          url: '/dropdown/client-types',
        });
        if (res && res.data) {
          let items = res.data;
          if (!Array.isArray(items)) {
            items = res.data?.data || res.data?.list || [];
          }
          if (Array.isArray(items)) {
            setClientTypes(items);
          }
        }
      } catch (err) {
      }
    })();
  }, []);

  // Fetch matters connected to this client
  const fetchMatters = useCallback(async () => {
    try {
      setIsMatterLoading(true);
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}/matters`,
      });
      if (resp.status === 200 && resp.data?.success) {
        setMatters(resp.data.matters || []);
      }
    } catch (err) {
    } finally {
      setIsMatterLoading(false);
    }
  }, [clientId]);

  // Fetch audit log for this client
  const fetchAuditLog = useCallback(async () => {
    try {
      setIsAuditLogLoading(true);
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}/audit-log`,
      });
      if (resp.status === 200 && resp.data?.success) {
        setAuditLog(resp.data.audit_log || []);
      }
    } catch (err) {
    } finally {
      setIsAuditLogLoading(false);
    }
  }, [clientId]);

  // Fetch billing information
  const fetchBilling = useCallback(async () => {
    try {
      setIsBillingLoading(true);
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}/billing`,
      });
      if (resp.status === 200 && resp.data?.success) {
        setBilling(resp.data.billing);
        setEditedBilling(resp.data.billing);
      }
    } catch (err) {
    } finally {
      setIsBillingLoading(false);
    }
  }, [clientId]);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      setIsDocumentLoading(true);
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}/documents`,
      });
      if (resp.status === 200 && resp.data?.success) {
        setDocuments(resp.data.documents || []);
      }
    } catch (err) {
    } finally {
      setIsDocumentLoading(false);
    }
  }, [clientId]);

  // Fetch user profile for a lawyer
  const fetchLawyerProfile = useCallback(async (lawyerUuid: string) => {
    try {
      const resp = await apis({
        method: 'GET',
        url: `/user-profile/user/uuid/${lawyerUuid}`,
      });
      if (resp.status === 200 && resp.data?.success && resp.data?.user_info) {
        setLawyerProfiles((prev) => ({
          ...prev,
          [lawyerUuid]: resp.data.user_info,
        }));
      }
    } catch (err) {
    }
  }, []);

  // Fetch lawyers for this client
  const fetchLawyers = useCallback(async () => {
    try {
      const resp = await apis({
        method: 'GET',
        url: `/clients/${clientId}/lawyers`,
      });
      if (resp.status === 200 && resp.data?.success) {
        const lawyersData = resp.data.lawyers || [];
        setLawyers(lawyersData);
        const uuids = lawyersData.map((l: any) => l.uuid);
        setSelectedLawyerIds(uuids);

        // Fetch profiles for lawyers with missing name/email data
        for (const lawyer of lawyersData) {
          if (!lawyer.name || !lawyer.email) {
            fetchLawyerProfile(lawyer.uuid);
          }
        }
      }
    } catch (err) {
    }
  }, [clientId, fetchLawyerProfile]);


  useEffect(() => {
    if (clientId) {
      fetchClientDetails();
    }
  }, [clientId]);

  useEffect(() => {
    if (client) {
      setBreadcrumbs([
        { name: t('Contacts'), href: '/contacts', current: false },
        { name: client.name || t('Unknown Client'), href: `/contacts/${clientId}`, current: true },
      ]);
    }
    return () => setBreadcrumbs([]);
  }, [client, clientId, setBreadcrumbs]);

  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  // Load tab-specific data on tab change
  useEffect(() => {
    if (activeTab === 'details') {
      // Fetch lawyers and available users
      (async () => {
        try {
          await fetchLawyers();
          await fetchAuditLog();
        } catch (err) {
          console.error('Error loading details tab data:', err);
        }
      })();
    } else if (activeTab === 'matters') {
      fetchMatters();
    } else if (activeTab === 'documents') {
      fetchDocuments();
    } else if (activeTab === 'billing') {
      fetchBilling();
    }
  }, [activeTab, clientId, fetchMatters, fetchDocuments, fetchBilling, fetchAuditLog]);

  const handleSaveInfo = async () => {
    if (!editedClient || !client) return;

    try {
      setIsSaving(true);
      const resp = await apis({
        method: 'PUT',
        url: `/clients/${client.uuid}`,
        data: {
          name: editedClient.name || client.name,
          type_id: editedClient.type_id || client.type_id,
          contact_email: editedClient.contact_email || client.contact_email,
          contact_phone: editedClient.contact_phone || client.contact_phone,
          address: editedClient.address || client.address,
          notes: editedClient.notes || client.notes,
          contact_person_name: editedClient.contact_person_name ?? client.contact_person_name ?? '',
          contact_person_email: editedClient.contact_person_email ?? client.contact_person_email ?? '',
        },
      });

      if (resp.status === 200 && resp.data?.success) {
        const updatedClient = resp.data.client || resp.data;
        setClient(updatedClient);
        setEditedClient(updatedClient);
        setIsEditingInfo(false);

        // Refresh lawyers data after client info is updated
        await fetchLawyers();

        dispatch(
          showFeedbackModal({
            showModal: true,
            modalType: 'success',
            message: t('Client information updated successfully'),
            duration: 3000,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        showFeedbackModal({
          showModal: true,
          modalType: 'error',
          message: t('Failed to update client information'),
          duration: 3000,
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClient = useCallback(() => {
    if (!client) return;

    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Delete Client'),
        subMsg: t('This client and all associated data will be permanently deleted. This action cannot be undone.'),
        icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
        isDest: true,
        onConfirm: async () => {
          try {
            await deleteClientAPI(client.uuid);
            dispatch(
              showFeedbackModal({
                showModal: true,
                message: t('Client deleted successfully'),
                modalType: 'success',
                duration: 3000,
              })
            );
            history.push('/contacts');
          } catch (error: any) {
            dispatch(
              showFeedbackModal({
                showModal: true,
                message: t('Failed to delete client'),
                modalType: 'error',
                duration: 3000,
              })
            );
          }
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  }, [client, dispatch, t, history]);


  const handleMatterConnected = () => {
    fetchMatters();
    dispatch(
      showFeedbackModal({
        showModal: true,
        modalType: 'success',
        message: t('Matter connected successfully'),
        duration: 3000,
      })
    );
  };

  const handleSaveBilling = async () => {
    if (!editedBilling || !client) return;

    try {
      setIsSaving(true);
      const resp = await apis({
        method: 'PUT',
        url: `/clients/${client.uuid}/billing`,
        data: editedBilling,
      });

      if (resp.status === 200 && resp.data?.success) {
        setBilling(editedBilling);
        setIsEditingBilling(false);
        dispatch(
          showFeedbackModal({
            showModal: true,
            modalType: 'success',
            message: t('Billing information updated successfully'),
            duration: 3000,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        showFeedbackModal({
          showModal: true,
          modalType: 'error',
          message: t('Failed to update billing information'),
          duration: 3000,
        })
      );
    } finally {
      setIsSaving(false);
    }
  };


  const handleDeleteDocument = useCallback((documentUuid: string) => {
    if (!client) return;

    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Delete Document'),
        subMsg: t('This document will be permanently deleted. This action cannot be undone.'),
        icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
        isDest: true,
        onConfirm: async () => {
          try {
            await apis({
              method: 'DELETE',
              url: `/clients/${client.uuid}/documents/${documentUuid}`,
            });

            await fetchDocuments();
            dispatch(
              showFeedbackModal({
                showModal: true,
                modalType: 'success',
                message: t('Document deleted successfully'),
                duration: 3000,
              })
            );
          } catch (err: any) {
            dispatch(
              showFeedbackModal({
                showModal: true,
                modalType: 'error',
                message: t('Failed to delete document'),
                duration: 3000,
              })
            );
          }
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  }, [client, dispatch, t, fetchDocuments]);

  const handleBulkDeleteDocuments = async () => {
    if (selectedDocuments.length === 0) return;

    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Delete Selected Documents'),
        subMsg: t('The selected documents will be permanently deleted. This action cannot be undone.'),
        icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
        isDest: true,
        onConfirm: async () => {
          setIsDeletingDocuments(true);
          try {
            for (const docUuid of selectedDocuments) {
              await apis({
                method: 'DELETE',
                url: `/clients/${client!.uuid}/documents/${docUuid}`,
              });
            }
            await fetchDocuments();
            setSelectedDocuments([]);
            dispatch(
              showFeedbackModal({
                showModal: true,
                modalType: 'success',
                message: t('Documents deleted successfully'),
                duration: 3000,
              })
            );
          } catch (err: any) {
            dispatch(
              showFeedbackModal({
                showModal: true,
                modalType: 'error',
                message: t('Failed to delete documents'),
                duration: 3000,
              })
            );
          } finally {
            setIsDeletingDocuments(false);
            dispatch(hideModal());
          }
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  };

  const handleOpenDocument = useCallback(async (documentUuid: string, filename: string) => {
    if (!client) return;

    try {
      const fileType = filename.split('.').pop()?.toLowerCase() || 'file';
      const result = await viewFile({
        domain: 'clients',
        resourceId: client.uuid,
        fileId: documentUuid,
        filename: filename,
        contentType: fileType,
      });

      if (!result.success || !result.blob) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: result.error || 'Failed to load document',
          showModal: true,
          duration: 3000,
        }));
        return;
      }

      const component = (
        <AttachmentViewer
          taskUuid={client.uuid}
          attachment={{
            id: documentUuid,
            FileName: filename,
            FileType: fileType,
            ContentType: result.contentType,
          }}
          blob={result.blob}
          contentType={result.contentType || fileType}
          filename={result.filename || filename}
          onDownload={async () => {
            await downloadFile({
              domain: 'clients',
              resourceId: client.uuid,
              fileId: documentUuid,
              filename: filename,
            });
          }}
        />
      );

      dispatch(
        showModal({
          message: filename,
          icon: <PiFolderLight className='w-6 h-6 text-gray-600' />,
          type: 'attachmentViewer',
          component,
          showModal: true,
        })
      );
    } catch (error: any) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error opening document. Please try again.'),
        showModal: true,
        duration: 3000,
      }));
    }
  }, [client, dispatch, viewFile, downloadFile]);

  const getClientTypeLabel = (typeId: number): string => {
    const clientType = clientTypes.find((ct) => ct.value === String(typeId) || ct.id === typeId);
    return clientType?.label || `Type ${typeId}`;
  };

  // Build breadcrumbs
  const clientBreadcrumbs = useMemo(() => [
    { name: t('Contacts'), href: '/contacts', current: false },
    { name: client?.name || t('Loading...'), href: `/contacts/${clientId}`, current: true },
  ], [client, clientId, t]);

  // Additional header elements with lawyer avatars
  const additionalHeaderElements = useMemo(() => (
    <div className="flex gap-3 items-center">
      {lawyers?.length > 0 && (
        <div className="flex items-center">
          {lawyers.slice(0, 3).map((lawyer: any, idx: number) => {
            const profileData = lawyerProfiles[lawyer.uuid];
            const lawyerName = lawyer.name || profileData?.first_name || profileData?.last_name
              ? profileData
                ? `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim()
                : (lawyer.first_name || lawyer.last_name ? `${lawyer.first_name} ${lawyer.last_name}`.trim() : '')
              : '';
            const displayName = lawyerName || `Person ${lawyer.uuid?.substring(0, 8)}`;
            const lawyerInitials = displayName
              .split(' ')
              .filter((part: string) => part)
              .map((part: string) => part.charAt(0))
              .join('')
              .toUpperCase() || 'L';
            return (
              <div key={idx} className={clsx('relative rounded-full', idx > 0 && 'ml-[-0.5rem]')}>
                <CollaboratorBadge collaborator={{
                  name: displayName,
                  initials: lawyerInitials,
                  email: lawyer.email || profileData?.email || '',
                  avatarURL: lawyer.profile_picture || profileData?.profile_picture || null
                }} />
              </div>
            );
          })}
          {lawyers.length > 3 && (
            <div className="relative ml-[-0.5rem]" title={lawyers.slice(3).map((l: any) => {
              const profileData = lawyerProfiles[l.uuid];
              return l.name || profileData?.first_name || profileData?.last_name
                ? profileData
                  ? `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim()
                  : (l.first_name || l.last_name ? `${l.first_name} ${l.last_name}`.trim() : '')
                : `Person ${l.uuid?.substring(0, 8)}`;
            }).join(', ')}>
              <div className="h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm text-gray-700 dark:text-white ">
                +{lawyers.length - 3}
              </div>
            </div>
          )}
        </div>
      )}
      <Menu as="div" className="relative inline-block text-left z-50" data-tour-id="client-actions-menu">
        <Menu.Button as={Button} variant="primary" size="small">
          <PiSliders className="h-4 w-4 mr-2" />
          {t('Actions')}
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
            <div className="px-1 py-1">
              {/* <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => {
                      setIsEditingInfo(true);
                      setActiveTab('information');
                    }}
                    className={`${
                      active
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                        : 'text-gray-700 dark:text-gray-300'
                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                  >
                    <PiPencilLight className="mr-2 h-4 w-4" />
                    {t('Edit')}
                  </button>
                )}
              </Menu.Item> */}
              {client?.is_active && (
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={async () => {
                        if (!client) return;
                        try {
                          setIsMatterLoading(true);
                          const res = await apis({
                            method: 'GET',
                            url: '/matters/view?page=1&limit=12',
                          });
                          let matters = [];
                          if (res && res.data) {
                            matters = Array.isArray(res.data.data) ? res.data.data : Array.isArray(res.data.matters) ? res.data.matters : [];
                          }
                          dispatch(showConnectMatterModal(
                            client.uuid,
                            client.name,
                            client.uuid,
                            matters,
                            () => {
                              fetchMatters();
                              dispatch(
                                showFeedbackModal({
                                  showModal: true,
                                  modalType: 'success',
                                  message: t('Matter connected successfully'),
                                  duration: 3000,
                                })
                              );
                            }
                          ));
                        } catch (err) {
                          console.error('Error fetching matters:', err);
                        } finally {
                          setIsMatterLoading(false);
                        }
                      }}
                      disabled={isMatterLoading}
                      className={`${
                        active
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                          : 'text-gray-700 dark:text-gray-300'
                      } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                    >
                      <PiLinkLight className="mr-2 h-4 w-4" />
                      {t('Connect Matter')}
                    </button>
                  )}
                </Menu.Item>
              )}
              {client?.is_active && (
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => {
                        if (!client) return;
                        history.push({
                          pathname: '/matters',
                          search: '?add=true',
                          state: { clientId: client.uuid, clientName: client.name }
                        });
                      }}
                      className={`${
                        active
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                          : 'text-gray-700 dark:text-gray-300'
                      } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                    >
                      <PiPlusLight className="mr-2 h-4 w-4" />
                      {t('Create Matter')}
                    </button>
                  )}
                </Menu.Item>
              )}
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => {
                      if (!client) return;
                      dispatch(showUploadClientDocumentModal(
                        client.uuid,
                        client.name,
                        () => {
                          fetchDocuments();
                          dispatch(
                            showFeedbackModal({
                              showModal: true,
                              modalType: 'success',
                              message: t('Documents uploaded successfully'),
                              duration: 3000,
                            })
                          );
                        }
                      ));
                    }}
                    className={`${
                      active
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                        : 'text-gray-700 dark:text-gray-300'
                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                  >
                    <PiFilePlusLight className="mr-2 h-4 w-4" />
                    {t('Upload Document')}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => {
                      const willDeactivate = client?.is_active !== false;
                      dispatch(
                        showModal({
                          showModal: true,
                          type: 'confirmation',
                          message: willDeactivate
                            ? t('Deactivate this client?')
                            : t('Reactivate this client?'),
                          subMsg: willDeactivate
                            ? t('This client will no longer be available for new matters.')
                            : t('This client will become available again for new matters.'),
                          icon: willDeactivate
                            ? <PiXCircleLight className="w-6 h-6 text-orange-600" />
                            : <PiCheckCircleLight className="w-6 h-6 text-green-600" />,
                          isDest: false,
                          onConfirm: async () => {
                            if (!client) return;
                            try {
                              const resp = await apis({
                                method: 'PUT',
                                url: `/clients/${client.uuid}`,
                                data: { is_active: !willDeactivate },
                              });

                              if (resp.status === 200) {
                                dispatch(
                                  showFeedbackModal({
                                    showModal: true,
                                    message: willDeactivate
                                      ? t('Client deactivated successfully')
                                      : t('Client reactivated successfully'),
                                    modalType: 'success',
                                    duration: 3000,
                                  })
                                );
                                fetchClientDetails();
                              }
                            } catch (error: any) {
                              dispatch(
                                showFeedbackModal({
                                  showModal: true,
                                  message: t('Failed to update client status'),
                                  modalType: 'error',
                                  duration: 3000,
                                })
                              );
                            }
                            dispatch(hideModal());
                          },
                          onCancel: () => dispatch(hideModal()),
                        })
                      );
                    }}
                    className={`${
                      active
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                        : 'text-gray-700 dark:text-gray-300'
                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                  >
                    {client?.is_active !== false ? (
                      <><PiXCircleLight className="mr-2 h-4 w-4 text-orange-600" />{t('Deactivate')}</>
                    ) : (
                      <><PiCheckCircleLight className="mr-2 h-4 w-4 text-green-600" />{t('Reactivate')}</>
                    )}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => {
                      if (!client) return;
                      setAuditTarget({ type: 'client', id: client.uuid, name: client.name || '' });
                    }}
                    className={`${
                      active
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                        : 'text-gray-700 dark:text-gray-300'
                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                  >
                    <PiClockCounterClockwiseLight className="mr-2 h-4 w-4" />
                    {t('Activity')}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleDeleteClient}
                    className={`${
                      active
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                        : 'text-gray-700 dark:text-gray-300'
                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                  >
                    <PiTrashLight className="mr-2 h-4 w-4 text-red-600" />
                    {t('Delete')}
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  ), [lawyers, t, dispatch, history, client, handleDeleteClient, lawyerProfiles, fetchClientDetails, isMatterLoading, fetchMatters, fetchDocuments]);

  // Sub header with status and badges
  const subHeaderRight = useMemo(() => (
    <div className="flex flex-row gap-2 items-center">
      <Badge color={client?.is_active ? 'green' : 'gray'}>
        {client?.is_active ? t('Active') : t('Inactive')}
      </Badge>
      <Badge color="gray">
        {getClientTypeLabel(client?.type_id || 0)}
      </Badge>
      {client?.is_individual !== false ? (
        <Badge color="gray">
          {client?.contact_email || t('No Email')}
        </Badge>
      ) : (
        client?.contact_person_name && (
          <Badge color="gray">
            {client.contact_person_name}
          </Badge>
        )
      )}
    </div>
  ), [client, t, getClientTypeLabel]);

  // Filter matters by search + filters before rendering
  const filteredMatters = useMemo(() => {
    const q = matterSearch.trim().toLowerCase();
    const statuses = matterFilters['Status'] || [];
    return matters.filter((m: any) => {
      if (q) {
        const title = (m.title || m.name || '').toLowerCase();
        const type = (m.type || '').toLowerCase();
        const category = (m.category_name || '').toLowerCase();
        if (!title.includes(q) && !type.includes(q) && !category.includes(q)) return false;
      }
      if (statuses.length > 0) {
        const status = m.status_name || (m.status === 'active' ? 'Active' : 'Open');
        if (!statuses.includes(status)) return false;
      }
      return true;
    });
  }, [matters, matterSearch, matterFilters]);

  const matterFilterSections = useMemo(() => {
    const statusSet = new Set<string>();
    matters.forEach((m: any) => {
      const status = m.status_name || (m.status === 'active' ? 'Active' : 'Open');
      statusSet.add(status);
    });
    return [
      {
        id: 'status',
        name: 'Status',
        label: t('Status'),
        options: Array.from(statusSet).map((s) => ({ value: s, label: s })),
      },
    ] as any;
  }, [matters, t]);

  const matterSortOptions = useMemo(() => [
    { value: 'name_asc', label: t('Name A-Z'), current: matterSort === 'name_asc' },
    { value: 'name_desc', label: t('Name Z-A'), current: matterSort === 'name_desc' },
    { value: 'created_desc', label: t('Newest first'), current: matterSort === 'created_desc' },
    { value: 'created_asc', label: t('Oldest first'), current: matterSort === 'created_asc' },
  ] as any, [t, matterSort]);

  // Prepare matters table data with rich formatting
  const mattersTableData = useMemo(() => {
    const sorted = [...filteredMatters];
    if (matterSort === 'name_asc' || matterSort === 'name_desc') {
      sorted.sort((a: any, b: any) => {
        const an = (a.title || a.name || '').toLowerCase();
        const bn = (b.title || b.name || '').toLowerCase();
        return matterSort === 'name_asc' ? an.localeCompare(bn) : bn.localeCompare(an);
      });
    } else if (matterSort === 'created_asc' || matterSort === 'created_desc') {
      sorted.sort((a: any, b: any) => {
        const ad = new Date(a.created_at || 0).getTime();
        const bd = new Date(b.created_at || 0).getTime();
        return matterSort === 'created_asc' ? ad - bd : bd - ad;
      });
    }
    return sorted.map((matter: any) => {
      const matterTitle = matter.title || matter.name || `Matter ${matter.id}`;
      const matterType = matter.type || t('No type');
      const jurisdictionData = getJurisdictionDisplay(matter.jurisdiction);
      const statusName = matter.status_name || (matter.status === 'active' ? 'Active' : 'Open');

      return {
        id: matter.matter_uuid || matter.uuid || matter.id,
        uuid: matter.matter_uuid || matter.uuid || matter.id,
        matter_uuid: matter.matter_uuid || matter.uuid || matter.id,
        'Matter Name': (
          <div className="flex items-center gap-2 group/main relative w-full min-w-0 pr-4">
            {jurisdictionData && (
              <Flag countryCode={jurisdictionData.countryCode} code={jurisdictionData.code} size="lg" />
            )}
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="text-gray-900 dark:text-gray-100 truncate" title={matterTitle}>
                {matterTitle}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                {matterType}
              </div>
            </div>
          </div>
        ),
        'Category': (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {matter.category_name || t('No category')}
          </span>
        ),
        'Status': (
          <Badge
            className='capitalize'
            color={statusToColorMap[statusName] || getStatusColor(statusName) as any || 'gray'}
          >
            {statusName}
          </Badge>
        ),
        'Created At': formatDate(matter.created_at),
      };
    });
  }, [filteredMatters, matterSort, t]);

  // Filter + sort documents
  const filteredDocuments = useMemo(() => {
    const q = documentSearch.trim().toLowerCase();
    const types = documentFilters['Type'] || [];
    let result = documents.filter((d: any) => {
      if (q) {
        const fname = (d.filename || '').toLowerCase();
        const uploader = (d.uploaded_by_user_name || '').toLowerCase();
        if (!fname.includes(q) && !uploader.includes(q)) return false;
      }
      if (types.length > 0) {
        const ftype = (d.file_type || (d.filename || '').split('.').pop() || '').toUpperCase();
        if (!types.includes(ftype)) return false;
      }
      return true;
    });
    if (documentSort === 'name_asc' || documentSort === 'name_desc') {
      result = [...result].sort((a: any, b: any) => {
        const an = (a.filename || '').toLowerCase();
        const bn = (b.filename || '').toLowerCase();
        return documentSort === 'name_asc' ? an.localeCompare(bn) : bn.localeCompare(an);
      });
    } else if (documentSort === 'date_asc' || documentSort === 'date_desc') {
      result = [...result].sort((a: any, b: any) => {
        const ad = new Date(a.uploaded_at || 0).getTime();
        const bd = new Date(b.uploaded_at || 0).getTime();
        return documentSort === 'date_asc' ? ad - bd : bd - ad;
      });
    }
    return result;
  }, [documents, documentSearch, documentFilters, documentSort]);

  const documentFilterSections = useMemo(() => {
    const typeSet = new Set<string>();
    documents.forEach((d: any) => {
      const ftype = (d.file_type || (d.filename || '').split('.').pop() || '').toUpperCase();
      if (ftype) typeSet.add(ftype);
    });
    return [
      {
        id: 'type',
        name: 'Type',
        label: t('Type'),
        options: Array.from(typeSet).map((s) => ({ value: s, label: s })),
      },
    ] as any;
  }, [documents, t]);

  const documentSortOptions = useMemo(() => [
    { value: 'name_asc', label: t('Name A-Z'), current: documentSort === 'name_asc' },
    { value: 'name_desc', label: t('Name Z-A'), current: documentSort === 'name_desc' },
    { value: 'date_desc', label: t('Newest first'), current: documentSort === 'date_desc' },
    { value: 'date_asc', label: t('Oldest first'), current: documentSort === 'date_asc' },
  ] as any, [t, documentSort]);

  // Determine whether current user can administer this contact's access list.
  // Admin = current user has access_level 'admin' OR is_lead on this client,
  // OR is a firm-level admin role.
  const canAdmin = useMemo(() => {
    const ui: any = userInfo || {};
    const candidateIds = new Set(
      [
        currentUserId,
        ui.UserID,
        ui.userId,
        ui.id,
        ui.UserUUID,
        ui.user_uuid,
        ui.uuid,
      ]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .map((v) => String(v))
    );
    const me = lawyers.find((l: any) =>
      candidateIds.has(String(l.uuid)) ||
      candidateIds.has(String(l.user_id)) ||
      candidateIds.has(String(l.id))
    );
    if (me) {
      const access = (me.access_level || me.role || '') as string;
      if (access === 'admin' || access === 'full_access') return true;
      if (me.is_lead) return true;
    }
    const role = ui?.Role || ui?.role || '';
    const userType = ui?.UserType || ui?.user_type || '';
    if (userType === 'LawFirmAdmin') return true;
    if (typeof role === 'string' && /admin|managing partner|owner/i.test(role)) return true;
    return false;
  }, [lawyers, currentUserId, userInfo]);

  if (isLoading || !client) return <SkeletonLoader />;

  // Humanize action names for display
  const humanizeAction = (action: string): string => {
    return action
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Tabs configuration
  const tabs = [
    { label: t('Details'), value: 'details', icon: <PiNoteLight className="h-4 w-4" /> },
    { label: t('Matters'), value: 'matters', icon: <PiFileTextLight className="h-4 w-4" /> },
    { label: t('Documents'), value: 'documents', icon: <PiFolderLight className="h-4 w-4" /> },
    { label: t('Billing'), value: 'billing', icon: <PiMoneyLight className="h-4 w-4" />, disabled: true },
  ];

  return (<>
    <DetailFrame
      breadcrumbs={clientBreadcrumbs}
      dropdownActions={[]}
      subscriptionActive={subscriptionActive}
      additionalHeaderElements={additionalHeaderElements}
      showBackButton={true}
      subHeaderRight={subHeaderRight}
    >
      <div className="flex-1 flex flex-col">
        {/* Tabs Navigation */}
        <div className="pt-2 px-4 dark:bg-gray-800 w-full relative overflow-visible z-10 border-b border-gray-200 dark:border-gray-700" data-tour-id="client-tabs">
          <div className="flex gap-3 pb-2">
            {tabs.map((tab: any) => {
              const TabButton = (
                <button
                  key={tab.value}
                  onClick={() => !tab.disabled && setActiveTab(tab.value)}
                  disabled={tab.disabled}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-1 text-xs rounded transition-all duration-200 border border-1',
                    tab.disabled
                      ? 'text-gray-400 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-60'
                      : clsx(
                          'hover:border-gray-300 hover:bg-white dark:hover:bg-black',
                          activeTab === tab.value
                            ? 'text-primary-600 dark:text-primary-300 bg-white dark:bg-black border-gray-300'
                            : 'text-gray-600 dark:text-gray-300 border-transparent hover:border-gray-300 dark:hover:bg-gray-700'
                        )
                  )}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              );

              if (tab.disabled) {
                return (
                  <Tooltip key={tab.value} title="Coming soon" arrow>
                    <span>
                      {TabButton}
                    </span>
                  </Tooltip>
                );
              }

              return TabButton;
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar-main bg-gray-50 dark:bg-gray-900">
          {/* Information Tab */}
          {activeTab === 'details' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 max-h-[calc(100vh-100px)] overflow-y-auto custom-scrollbar-main px-4 py-4" data-tour-id="client-details-tab">

              {/* ── Left Column ── */}
              <div className="lg:col-span-2 space-y-5">

                {/* ── Notes / Info Card ── */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                  <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                      <PiTextAlignLeftLight className="h-4 w-4" />
                      <span className="text-xs font-medium">{t('Client Information')}</span>
                    </div>
                    {!isEditingInfo && (
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => setIsEditingInfo(true)}
                        icon={<PiPencilLight className="h-3.5 w-3.5" />}
                      >
                        {t('Edit')}
                      </Button>
                    )}
                  </div>

                  {isEditingInfo ? (
                    <div className="p-5 space-y-4">
                      <Input
                        label={editedClient?.is_individual !== false ? t('Client Name') : t('Company / Organization Name')}
                        type="text"
                        value={editedClient?.name || ''}
                        onChange={(e) =>
                          setEditedClient({ ...editedClient, name: e.target.value })
                        }
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Dropdown
                          label={t('Client Type')}
                          options={clientTypes}
                          value={String(editedClient?.type_id || '')}
                          hierarchical={true}
                          onSelect={(option) => {
                            const newTypeId = parseInt(option.value);
                            const individualTypeIds = new Set([6, 54, 55, 41, 25, 73, 19, 63, 42]);
                            const isIndividual = individualTypeIds.has(newTypeId);
                            setEditedClient({
                              ...editedClient,
                              type_id: newTypeId,
                              is_individual: isIndividual,
                            });
                          }}
                          placeholder={t('Select type')}
                        />
                        <Input
                          label={editedClient?.is_individual !== false ? t('Email') : t('Company Email')}
                          type="email"
                          value={editedClient?.contact_email || ''}
                          onChange={(e) =>
                            setEditedClient({ ...editedClient, contact_email: e.target.value })
                          }
                        />
                      </div>

                      {/* Contact Person section - only for business/org clients */}
                      {editedClient?.is_individual === false && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100 dark:border-gray-700/60">
                          <Input
                            label={t('Contact Person Name')}
                            type="text"
                            value={editedClient?.contact_person_name || ''}
                            onChange={(e) =>
                              setEditedClient({ ...editedClient, contact_person_name: e.target.value })
                            }
                          />
                          <Input
                            label={t('Contact Person Email')}
                            type="email"
                            value={editedClient?.contact_person_email || ''}
                            onChange={(e) =>
                              setEditedClient({ ...editedClient, contact_person_email: e.target.value })
                            }
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label={t('Phone')}
                          type="tel"
                          value={editedClient?.contact_phone || ''}
                          onChange={(e) =>
                            setEditedClient({ ...editedClient, contact_phone: e.target.value })
                          }
                        />
                        <Input
                          label={t('Address')}
                          type="text"
                          value={editedClient?.address || ''}
                          onChange={(e) =>
                            setEditedClient({ ...editedClient, address: e.target.value })
                          }
                        />
                      </div>

                      <Textarea
                        id="client-notes"
                        label={t('Notes')}
                        value={editedClient?.notes || ''}
                        onChange={(e) =>
                          setEditedClient({ ...editedClient, notes: e.target.value })
                        }
                        placeholder={t('Add any additional information about this client')}
                      />

                      <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700/60">
                        <Button
                          variant="neutral"
                          size="small"
                          onClick={() => {
                            setIsEditingInfo(false);
                            setEditedClient(client);
                          }}
                        >
                          {t('Cancel')}
                        </Button>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={handleSaveInfo}
                          disabled={isSaving}
                        >
                          {isSaving ? t('Saving...') : t('Save Changes')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-5">
                      {client.notes ? (
                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap max-w-prose">
                          {client.notes}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                          {t('No notes added')}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Identity Section ── */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 px-1">
                    <PiTagLight className="h-4 w-4" />
                    <span className="text-xs font-medium">{t('Identity')}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-3 min-w-0">
                      <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">{t('Type')}</p>
                      <p className="text-sm text-gray-700 dark:text-gray-200 truncate">{getClientTypeLabel(client.type_id)}</p>
                    </div>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-3 min-w-0">
                      <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">{t('Status')}</p>
                      <Badge color={client.is_active ? 'green' : 'gray'}>
                        {client.is_active ? t('Active') : t('Inactive')}
                      </Badge>
                    </div>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-3 min-w-0">
                      <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">{t('Onboarded')}</p>
                      <Badge color={client.client_metadata?.IsOnboarded ? 'green' : 'yellow'}>
                        {client.client_metadata?.IsOnboarded ? t('Yes') : t('No')}
                      </Badge>
                    </div>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-3 min-w-0">
                      <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">{t('Created')}</p>
                      <p className="text-sm text-gray-700 dark:text-gray-200 truncate">
                        {client.created_at ? formatDateTime(client.created_at) : '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* ── Contact Section ── */}
                {(client.contact_email || client.contact_phone || client.contact_person_name || client.address) && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 px-1">
                      <PiEnvelopeLight className="h-4 w-4" />
                      <span className="text-xs font-medium">{t('Contact')}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {client.contact_email && (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-3 min-w-0">
                          <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">
                            {client.is_individual !== false ? t('Email') : t('Company Email')}
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-200 truncate" title={client.contact_email}>
                            {client.contact_email}
                          </p>
                        </div>
                      )}
                      {client.contact_phone && (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-3 min-w-0">
                          <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">{t('Phone')}</p>
                          <p className="text-sm text-gray-700 dark:text-gray-200 truncate">{client.contact_phone}</p>
                        </div>
                      )}
                      {client.is_individual === false && client.contact_person_name && (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-3 min-w-0">
                          <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">{t('Contact Person')}</p>
                          <p className="text-sm text-gray-700 dark:text-gray-200 truncate" title={client.contact_person_name}>
                            {client.contact_person_name}
                          </p>
                        </div>
                      )}
                      {client.address && (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-3 min-w-0 col-span-2 sm:col-span-4">
                          <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">{t('Address')}</p>
                          <p className="text-sm text-gray-700 dark:text-gray-200 truncate" title={client.address}>{client.address}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Recent Activity Card ── */}
                <RecentActivityCard
                  fetchEntries={async () => auditLog.slice(0, 5).map((e: any) => ({
                    id: e.id,
                    action: e.action,
                    details: e.details,
                    created_at: e.created_at,
                    user_name: e.created_by_user_name,
                  }))}
                  onViewAll={() => client && setAuditTarget({ type: 'client', id: client.uuid, name: client.name || '' })}
                />

                {/* ── Last Updated ── */}
                {client.updated_at && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                    <div className="flex items-center justify-between px-5 py-3 text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <PiClockClockwiseLight className="h-4 w-4" />
                        <span className="text-xs font-medium">{t('Last Updated')}</span>
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-200">
                        {formatDateTime(client.updated_at)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Right Column — Access ── */}
              <div className="space-y-5 h-fit">
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                      <PiUsersThreeLight className="h-4 w-4" />
                      <span className="text-xs font-medium">{t('Access')}</span>
                    </div>
                    {canAdmin && (
                      <Button
                        variant="outline"
                        size="small"
                        onClick={() => {
                          const adapter = buildClientCollaboratorAdapter({
                            clientId: clientId!,
                            lawyers,
                            currentUserUuid: currentUserId || undefined,
                            canAdmin,
                            onUpdate: () => { fetchLawyers(); },
                            subMessage: t('Add or remove people who can access this contact.'),
                          });
                          dispatch(showSidePanel({
                            type: 'manageResourceCollaborators',
                            showSidePanel: true,
                            width: '50%',
                            message: t('Manage Access'),
                            icon: <PiUsersThreeLight className="w-5 h-5" />,
                            ...adapter,
                          } as any));
                        }}
                      >
                        {t('Manage')}
                      </Button>
                    )}
                  </div>
                  <div className="p-4 space-y-2.5">
                    {lawyers.length === 0 ? (
                      <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                        {t('No people assigned yet.')}
                      </p>
                    ) : (
                      lawyers.map((lawyer: any) => {
                        const name = lawyer.name || `${lawyer.first_name || ''} ${lawyer.last_name || ''}`.trim() || lawyer.email || '';
                        const inits = lawyer.initials || `${(lawyer.first_name || '').charAt(0)}${(lawyer.last_name || '').charAt(0)}`.toUpperCase() || '??';
                        return (
                          <div key={lawyer.uuid} className="flex items-center gap-2.5">
                            <ProfileImage
                              profilePicture={lawyer.profile_picture || null}
                              email={lawyer.email || ''}
                              initials={inits}
                              firstName={lawyer.first_name}
                              lastName={lawyer.last_name}
                              size="w-7 h-7"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{name}</p>
                              {lawyer.email && (
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{lawyer.email}</p>
                              )}
                            </div>
                            {(() => {
                              const raw = lawyer.access_level || lawyer.role || 'view';
                              const access = raw === 'edit' || raw === 'edit_access' ? 'edit'
                                : raw === 'admin' || raw === 'full_access' ? 'admin'
                                : 'view';
                              const label = access === 'edit' ? t('Edit') : access === 'admin' ? t('Admin') : t('View');
                              const color: any = access === 'edit' ? 'blue' : access === 'admin' ? 'slate' : 'gray';
                              return (
                                <div className="flex items-center gap-1.5">
                                  {lawyer.is_lead && <Badge color="blue">{t('Lead')}</Badge>}
                                  <Badge color={color}>{label}</Badge>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Matters Tab */}
          {activeTab === 'matters' && (
            <div data-tour-id="client-matters-tab">
              {isMatterLoading ? (
                <div className="space-y-4 px-4">
                  <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade"></div>
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade"></div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <FilterBarWithTabs
                    filters={matterFilterSections}
                    sortOptions={matterSortOptions}
                    selectedFilters={matterFilters}
                    onFilterChange={(name, vals) => setMatterFilters((prev) => ({ ...prev, [name]: vals }))}
                    onSortChange={setMatterSort}
                    onSearchChange={setMatterSearch}
                    sortValue={matterSort}
                    onClearAllFilters={() => { setMatterFilters({}); setMatterSearch(''); setMatterSort(''); }}
                    itemCount={mattersTableData.length}
                    totalItemCount={matters.length}
                    itemCountLabel={t('matters')}
                    noBorder
                  />

                  {mattersTableData.length > 0 ? (
                    <Table
                      columns={['Matter Name', 'Category', 'Status', 'Created At']}
                      data={mattersTableData}
                      onRowClick={(row) => history.push(`/matters/${row.matter_uuid}`)}
                      sortableColumns={['Matter Name', 'Created At']}
                      sortBy="Matter Name"
                      sortOrder="asc"
                      onSort={() => {}}
                      resizableColumns={true}
                    />
                  ) : matters.length > 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-600 dark:text-gray-400">
                        {t('No matters match your filters.')}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <PiFileTextLight className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">
                        {t('No matters connected to this client yet.')}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div data-tour-id="client-documents-tab">
              {isDocumentLoading ? (
                <div className="space-y-4 px-4">
                  <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade"></div>
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade"></div>
                    ))}
                  </div>
                </div>
              ) : (
              <>
              <FilterBarWithTabs
                filters={documentFilterSections}
                sortOptions={documentSortOptions}
                selectedFilters={documentFilters}
                onFilterChange={(name, vals) => setDocumentFilters((prev) => ({ ...prev, [name]: vals }))}
                onSortChange={setDocumentSort}
                onSearchChange={setDocumentSearch}
                sortValue={documentSort}
                onClearAllFilters={() => { setDocumentFilters({}); setDocumentSearch(''); setDocumentSort(''); }}
                itemCount={filteredDocuments.length}
                totalItemCount={documents.length}
                itemCountLabel={t('documents')}
                noBorder
              />
              <Table
                columns={['Filename', 'Type', 'Uploaded By', 'Date', 'Actions']}
                data={filteredDocuments.map((doc) => {
                  const { name: formattedName, extension } = formatDocumentName(doc.filename);
                  return {
                  id: doc.document_uuid,
                  Filename: formattedName,
                  FilenameExtension: extension,
                  Type: doc.file_type?.toUpperCase() || extension?.toUpperCase() || 'FILE',
                  'Uploaded By': doc.uploaded_by_user_name || 'Unknown',
                  Date: formatDateTime(doc.uploaded_at),
                  ...doc,
                };
                })}
                renderCell={(column, value, row) => {
                  if (column === 'Filename') {
                    return (
                      <div
                        className="cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        onClick={() => handleOpenDocument(row.document_uuid, row.filename)}
                      >
                        <p className="text-sm text-gray-900 dark:text-white">
                          {value}
                          {row.FilenameExtension && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({row.FilenameExtension.toUpperCase()})</span>}
                        </p>
                      </div>
                    );
                  }
                  if (column === 'Type') {
                    return <Badge color="gray">{value}</Badge>;
                  }
                  return value;
                }}
                actions={[
                  {
                    label: t('Delete'),
                    icon: <PiTrashLight className="w-4 h-4" />,
                    onClick: (row) => handleDeleteDocument(row.document_uuid),
                    isDestructive: true,
                  },
                ]}
                emptyState={{
                  icon: <PiFolderLight className="h-16 w-16 text-gray-400" />,
                  headline: t('No documents uploaded yet'),
                  subtext: t('Use the uploader above to add documents to this client'),
                }}
                resizableColumns={true}
                setIsShowing={true}
                total_items={documents.length}
                total_showing_items={documents.length}
                showingLabel={t('documents')}
              />
              </>
              )}
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <div className="flex items-center justify-center h-[calc(100vh-250px)]">
              <div className="text-center max-w-md mx-auto space-y-4">
                <PiMoneyLight className="h-16 w-16 text-gray-400 dark:text-gray-600 mx-auto" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('Billing Coming Soon')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('The billing feature is currently in development and will be available soon. Check back later for billing and invoicing management.')}
                </p>
              </div>
            </div>
          )}

        </div>
      </div>
    </DetailFrame>
    <FeatureTour
      steps={clientDetailsTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    {auditTarget && (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-20 z-40" onClick={() => setAuditTarget(null)} />
        <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-900 shadow-xl z-50 animate-slide-in-right">
          <AuditLogPanel
            entityType={auditTarget.type}
            entityId={auditTarget.id}
            entityName={auditTarget.name}
            onClose={() => setAuditTarget(null)}
          />
        </div>
      </>
    )}
  </>);
};

export default ClientDetails;
